import type { DependencyList } from "react";
import { createLocalPersister } from "tinybase/persisters/persister-browser/with-schemas";
import * as UIReact from "tinybase/ui-react/with-schemas";
import {
    type Value,
    type Values,
    type Store,
} from "tinybase/with-schemas";
import { useSetTask } from "tinytick/ui-react";
import { toast } from "sonner";
import { client } from "@/utils/orpc";
import { useNetworkState } from "@uidotdev/usehooks";
import {
    AUTH_VALUES_SCHEMA,
    type AuthSchema,
    type UserSchema,
} from "@/stores/schemas";

const REDIRECT_URI = import.meta.env.VITE_AUTH_REDIRECT_URI;

const STORE_NAME = "auth";

export const {
    useCreatePersister: useCreateAuthPersister,
    useCreateStore: useCreateAuthStore,
    useProvideStore: useProvideStore,
    useValue,
    useValues,
    useSetValueCallback,
    useStore,
} = UIReact as UIReact.WithSchemas<AuthSchema>;

type ValueIDs = keyof typeof AUTH_VALUES_SCHEMA;

export const useAuthStore = () => {
    return useStore();
}

export const useAuthValue = <ValueID extends ValueIDs>(valueID: ValueID) => {
    return useValue(valueID, STORE_NAME);
}

export const useAuthValues = (): Values<AuthSchema[1]> => {
    return useValues(STORE_NAME);
}

export const useSetAuthValueCallback = <Parameter, ValueID extends ValueIDs>
    (
        valueID: ValueID,
        getValue: (parameter: Parameter) => Value<AuthSchema[1], ValueID>,
        getValueDeps?: DependencyList
    ) => {
    useSetValueCallback(valueID, getValue, getValueDeps, STORE_NAME);
}

export const AuthStore = ({ authStore, userStore }: { authStore: Store<AuthSchema>, userStore: Store<UserSchema> }) => {
    const network = useNetworkState();

    useCreateAuthPersister(
        authStore,
        (store) => createLocalPersister(store, STORE_NAME),
        [],
        async (persister) => {
            await persister.startAutoLoad();
            await persister.startAutoSave();
        }
    );

    useSetTask(
        "initAuth",
        async () => {
            await initAuth(authStore);
        },
        [authStore],
        "auth",
    );

    useSetTask(
        "handleOAuthCallback",
        async (code) =>
            await handleOAuthCallback(code!, authStore, userStore),
        [authStore],
        "auth", {
        maxDuration: 3000
    }
    )

    useSetTask(
        "refreshToken",
        async () => {
            if (!network.online) {
                return;
            }
            await refreshAccessToken(authStore.getValue("refreshToken"), authStore);
        },
        [authStore],
        "auth",
        {
            repeatDelay: 50 * 60 * 1000,
            maxRetries: 3,
        },
        [authStore.getValue("refreshToken")]
    )

    useSetTask(
        "logout",
        async () => {
            logout(authStore);
        },
        [authStore],
        "auth",
    )

    useProvideStore(STORE_NAME, authStore);
    return null;
}

// Auth Actions
const initAuth = async (store: Store<AuthSchema>) => {
    const expiresAt = store.getValue("expiresAt");
    const refreshToken = store.getValue("refreshToken");

    if (expiresAt && expiresAt > Date.now()) {
        store.transaction(() => {
            store
                .setValue("isAuthenticated", true)
                .setValue("isLoading", false);
        });
    } else if (refreshToken) {
        store.setValue("isLoading", true);
        try {
            await refreshAccessToken(refreshToken, store);
            store.setValue("isAuthenticated", true);
        } finally {
            // Always clear loading, irrespective of success / failure
            store.setValue("isLoading", false);
        }
    } else {
        store.transaction(() => {
            store
                .setValue("isAuthenticated", false)
                .setValue("isLoading", false);
        });
    }

    store.setValue("isLoading", false);
}

export const login = async () => {
    try {
        const state = crypto.randomUUID();

        // Store OAuth State in Session Storage
        sessionStorage.setItem("oauthState", state);

        if (!REDIRECT_URI) {
            throw new Error("Redirect URI not found");
        }

        const authUrl = await client.auth.createAuthorizationUrl({
            redirectUri: REDIRECT_URI,
            state: state,
        });

        window.location.href = authUrl;
    } catch (error) {
        console.error("[auth] Error Logging In:", error);
    }
}

export const logout = (store: Store<AuthSchema>) => {
    clearAuthState(store);
    window.location.href = "/";
}

export const handleOAuthCallback = async (code: string, store: Store<AuthSchema>, userStore: Store<UserSchema>): Promise<void> => {
    try {
        const state = sessionStorage.getItem("oauthState");

        if (!state) {
            throw new Error("State Not Found");
        }

        if (!REDIRECT_URI) {
            throw new Error("Redirect URI Not Found");
        }

        const authResponse = await client.auth.exchangeToken({
            code: code,
            redirectUri: REDIRECT_URI,
        });

        store.transaction(() => {
            store.setPartialValues({
                accessToken: authResponse.access_token,
                refreshToken: authResponse.refresh_token,
                expiresAt: Date.now() + authResponse.expires_in * 1000,
                isAuthenticated: true,
                isLoading: false
            });
        });

        const userResponse = await client.auth.getUserInfo({
            accessToken: authResponse.access_token,
        });

        userStore?.transaction(() => {
            userStore?.setPartialValues({
                id: userResponse.id,
                email: userResponse.email,
                name: userResponse.name,
                given_name: userResponse.given_name ?? "",
                family_name: userResponse.family_name ?? "",
                picture: userResponse.picture ?? "",
                roles: JSON.stringify(userResponse.roles) ?? "",
            });
        });

        store.setValue("setupCompleted", true);

        toast.success('Authentication Successful');

    } catch (error: any) {
        console.error("[auth] Error Handling OAuth Callback:", error);
        throw error;
    }
}

export const refreshAccessToken = async (token: string, authStore: Store<AuthSchema>) => {
    try {
        if (!navigator.onLine) {
            authStore.setValue("isLoading", false);
            return;
        }

        console.log("[auth] Refreshing Access Token");
        const refreshToken = token;

        if (!refreshToken) {
            throw new Error("Refresh Token Not Found");
        }

        if (authStore.getValue("expiresAt") > Date.now() + 10 * 60 * 1000) {
            return;
        }

        const authResponse = await client.auth.refreshToken({
            refreshToken: refreshToken,
        });

        authStore.transaction(() => {
            authStore.setPartialValues({
                accessToken: authResponse.access_token,
                refreshToken: authResponse.refresh_token,
                expiresAt: Date.now() + authResponse.expires_in * 1000,
                isAuthenticated: true,
                isLoading: false,
            });
        });

        toast.success("Token Refreshed");
    } catch (error) {
        console.error("[auth] Error Refreshing Access Token:", error);
        clearAuthState(authStore);
        toast.error("Error Refreshing Access Token");
        throw error;
    }
}

const clearAuthState = (store: Store<AuthSchema>) => {
    store.setPartialValues({
        accessToken: "",
        refreshToken: "",
        expiresAt: 0,
        isAuthenticated: false,
        isLoading: false,
    })
};
